import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import API from '../lib/API';
import { setBoutiqueItems, setSupplyOrders, setSupplyItems, setCustomerOrders } from '../slices/boutiquesSlice';
import { BoutiqueItems, SupplyOrders, CustomerOrders } from '../types';

import Container from 'react-bootstrap/Container';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import NavBar, { tabs } from '../components/MainNavBar';
import BoutiqueItemFormModal from '../components/BoutiqueItemFormModal';
import SupplyOrderFormModal from '../components/SupplyOrderFormModal';
import CustomerOrderFormModal from '../components/CustomerOrderFormModal';
import ConfirmationModal from '../components/ConfirmationModal';
import BoutiqueItemImageModal from '../components/BoutiqueItemImageModal';
import Card from 'react-bootstrap/Card';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import Collapse from 'react-bootstrap/Collapse';

const BoutiqueDetailsView: React.FC = () => {
    const { boutiqueId } = useParams<{ boutiqueId: string }>();
    const dispatch = useDispatch();
    const token = useSelector((state: RootState) => state.status.token);
    const boutiques = useSelector((state: RootState) => state.boutiques.boutiques);
    const boutiqueItems = useSelector((state: RootState) => state.boutiques.boutiqueItems);
    const supplyOrders = useSelector((state: RootState) => state.boutiques.supplyOrders);
    const customerOrders = useSelector((state: RootState) => state.boutiques.customerOrders);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [showItemModal, setShowItemModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<BoutiqueItems | undefined>(undefined);
    const [showSupplyOrderModal, setShowSupplyOrderModal] = useState(false);
    const [selectedSupplyOrder, setSelectedSupplyOrder] = useState<SupplyOrders | undefined>(undefined);
    const [showCustomerOrderModal, setShowCustomerOrderModal] = useState(false);
    const [selectedCustomerOrder, setSelectedCustomerOrder] = useState<CustomerOrders | undefined>(undefined);
    const [expandedSections, setExpandedSections] = useState({
        customerOrders: true,
        supplyOrders: false,
        items: false
    });
    const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
    const [itemFilter, setItemFilter] = useState('');
    const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<BoutiqueItems | undefined>(undefined);
    const [showItemImageModal, setShowItemImageModal] = useState(false);
    const [selectedImageItem, setSelectedImageItem] = useState<BoutiqueItems | undefined>(undefined);
    
    const boutique = boutiques.find(b => b.boutiqueId === Number(boutiqueId));

    useEffect(() => {
        const fetchBoutiqueData = async () => {
            if (!token || !boutiqueId) return;

            setLoading(true);
            setError('');

            try {
                const [
                    itemsResponse,
                    supplyOrdersResponse,
                    supplyItemsResponse,
                    customerOrdersResponse
                ] = await Promise.all([
                    API.getBoutiqueItems(token, Number(boutiqueId)),
                    API.getSupplyOrders(token, Number(boutiqueId)),
                    API.getSupplyItems(token, Number(boutiqueId)),
                    API.getCustomerOrders(token, Number(boutiqueId))
                ]);

                dispatch(setBoutiqueItems(itemsResponse));
                dispatch(setSupplyOrders(supplyOrdersResponse));
                dispatch(setSupplyItems(supplyItemsResponse));
                dispatch(setCustomerOrders(customerOrdersResponse));
            } catch (error) {
                setError(error instanceof Error ? error.message : 'An error occurred while fetching boutique data');
            } finally {
                setLoading(false);
            }
        };

        fetchBoutiqueData();
    }, [dispatch, token, boutiqueId]);

    const handleNewItem = () => {
        setSelectedItem(undefined);
        setShowItemModal(true);
    };

    const handleEditItem = (item: BoutiqueItems) => {
        setSelectedItem(item);
        setShowItemModal(true);
    };

    const handleItemImages = (item: BoutiqueItems) => {
        setSelectedImageItem(item);
        setShowItemImageModal(true);
    };

    const handleDeleteItemClick = (item: BoutiqueItems) => {
        setItemToDelete(item);
        setShowDeleteItemModal(true);
    };

    const handleDeleteItemCancel = () => {
        setItemToDelete(undefined);
        setShowDeleteItemModal(false);
    };

    const handleDeleteItemConfirm = async () => {
        if (!token || !boutiqueId || !itemToDelete) return;

        setError('');

        try {
            await API.deleteBoutiqueItem(token, itemToDelete.boutiqueItemId);
            const updatedItems = await API.getBoutiqueItems(token, Number(boutiqueId));
            dispatch(setBoutiqueItems(updatedItems));
            handleDeleteItemCancel();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while deleting the item');
            handleDeleteItemCancel();
        }
    };

    const handleNewSupplyOrder = () => {
        setSelectedSupplyOrder(undefined);
        setShowSupplyOrderModal(true);
    };

    const handleEditSupplyOrder = (order: SupplyOrders) => {
        setSelectedSupplyOrder(order);
        setShowSupplyOrderModal(true);
    };

    const toggleSection = (section: keyof typeof expandedSections) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const renderCustomerOrders = () => {
        return (
            <Card className="mb-4">
                <Card.Header 
                    className="d-flex justify-content-between align-items-center"
                    onClick={() => toggleSection('customerOrders')}
                    style={{ cursor: 'pointer' }}
                >
                    <div className="d-flex align-items-center">
                        <span className="me-2">{expandedSections.customerOrders ? '▼' : '▶'}</span>
                        <h5 className="mb-0">Customer Orders</h5>
                    </div>
                    <Button 
                        variant="primary" 
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCustomerOrder(undefined);
                            setShowCustomerOrderModal(true);
                        }}
                    >
                        New Customer Order
                    </Button>
                </Card.Header>
                <Collapse in={expandedSections.customerOrders}>
                    <Card.Body>
                        <Table hover responsive>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Client Name</th>
                                    <th>Phone</th>
                                    <th>Status</th>
                                    <th>Total Items</th>
                                    <th>Total Amount</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {customerOrders.map((order) => {
                                    const isExpanded = expandedSections.customerOrders && order.customerOrderId === expandedOrderId;
                                    const orderItems = Array.isArray(order.items) ? order.items : [];
                                    const totalItems = orderItems.reduce((sum, item) => sum + item.quantity, 0);
                                    const totalAmount = orderItems.reduce((sum, item) => sum + (item.quantity * item.salePrice), 0);

                                    return (
                                        <React.Fragment key={order.customerOrderId}>
                                            <tr>
                                                <td>
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedOrderId(isExpanded ? null : order.customerOrderId);
                                                        }}
                                                    >
                                                        {isExpanded ? '▼' : '▶'}
                                                    </Button>
                                                </td>
                                                <td
                                                    onClick={() => {
                                                        setSelectedCustomerOrder(order);
                                                        setShowCustomerOrderModal(true);
                                                    }}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {order.clientName}
                                                </td>
                                                <td>{order.customerPhone || '-'}</td>
                                                <td>
                                                    <Badge bg={
                                                        order.status === 'completed' ? 'success' :
                                                        order.status === 'processing' ? 'primary' :
                                                        order.status === 'cancelled' ? 'danger' :
                                                        'warning'
                                                    }>
                                                        {order.status}
                                                    </Badge>
                                                </td>
                                                <td>{totalItems}</td>
                                                <td>{totalAmount.toFixed(2)}</td>
                                                <td>{new Date(order.created).toLocaleDateString()}</td>
                                            </tr>
                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan={7} className="p-0">
                                                        <div className="bg-light p-3">
                                                            <Table size="sm" className="mb-0">
                                                                <thead>
                                                                    <tr>
                                                                        <th>Item Name</th>
                                                                        <th>Quantity</th>
                                                                        <th>Sale Price</th>
                                                                        <th>Total</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {orderItems.map((item, index) => {
                                                                        const boutiqueItem = boutiqueItems.find(bi => bi.boutiqueItemId === item.boutiqueItemId);
                                                                        return (
                                                                            <tr key={index}>
                                                                                <td>{boutiqueItem?.name || 'Unknown Item'}</td>
                                                                                <td>{item.quantity}</td>
                                                                                <td>{item.salePrice.toFixed(2)}</td>
                                                                                <td>{(item.quantity * item.salePrice).toFixed(2)}</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                    {orderItems.length === 0 && (
                                                                        <tr>
                                                                            <td colSpan={4} className="text-center">No items in this order</td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                {customerOrders.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="text-center">No customer orders found</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Collapse>
            </Card>
        );
    };

    const filteredItems = boutiqueItems.filter(item => 
        item.name.toLowerCase().includes(itemFilter.toLowerCase())
    );

    if (!boutique) {
        return (
            <>
                <NavBar tab={tabs.boutiques} />
                <Container className="mt-4">
                    <h2 className="text-center">Boutique not found</h2>
                </Container>
            </>
        );
    }

    return (
        <>
            <NavBar tab={tabs.boutiques} />
            <Container className="mt-4">
                <h2 className="text-center mb-4">{boutique.name}</h2>
                {loading ? (
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : (
                    <div>
                        <div className="mb-4">
                            <h3>Details</h3>
                            <p>Created: {new Date(boutique.created).toLocaleDateString()}</p>
                            <p>Status: {boutique.status}</p>
                        </div>

                        {renderCustomerOrders()}

                        <Card className="mb-4">
                            <Card.Header 
                                className="d-flex justify-content-between align-items-center"
                                onClick={() => toggleSection('supplyOrders')}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="d-flex align-items-center">
                                    <span className="me-2">{expandedSections.supplyOrders ? '▼' : '▶'}</span>
                                    <h5 className="mb-0">Supply Orders</h5>
                                </div>
                                <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleNewSupplyOrder();
                                    }}
                                >
                                    New Supply Order
                                </Button>
                            </Card.Header>
                            <Collapse in={expandedSections.supplyOrders}>
                                <Card.Body>
                                    <Table hover responsive>
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Status</th>
                                                <th>Exchange Rate</th>
                                                <th>Order Currency</th>
                                                <th>Sale Currency</th>
                                                <th>Created</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {supplyOrders.map((order) => (
                                                <tr 
                                                    key={order.supplyOrderId}
                                                    onClick={() => handleEditSupplyOrder(order)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>{order.supplyOrderId}</td>
                                                    <td>{order.status}</td>
                                                    <td>{Number(order.exchangeRate).toFixed(2)}</td>
                                                    <td>{order.orderCurrency}</td>
                                                    <td>{order.saleCurrency}</td>
                                                    <td>{new Date(order.created).toLocaleDateString()}</td>
                                                </tr>
                                            ))}
                                            {supplyOrders.length === 0 && (
                                                <tr>
                                                    <td colSpan={6} className="text-center">No supply orders found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Collapse>
                        </Card>

                        <Card className="mb-4">
                            <Card.Header 
                                className="d-flex justify-content-between align-items-center"
                                onClick={() => toggleSection('items')}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="d-flex align-items-center">
                                    <span className="me-2">{expandedSections.items ? '▼' : '▶'}</span>
                                    <h5 className="mb-0">Items</h5>
                                </div>
                                <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleNewItem();
                                    }}
                                >
                                    New Item
                                </Button>
                            </Card.Header>
                            <Collapse in={expandedSections.items}>
                                <Card.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            type="text"
                                            placeholder="Search items..."
                                            value={itemFilter}
                                            onChange={(e) => setItemFilter(e.target.value)}
                                        />
                                    </Form.Group>
                                    <Table hover responsive>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Description</th>
                                                <th>Price</th>
                                                <th>Discount</th>
                                                <th>Created</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredItems.map((item) => (
                                                <tr key={item.boutiqueItemId}>
                                                    <td>
                                                        <strong>{item.name}</strong>
                                                    </td>
                                                    <td>
                                                        {item.description ? (
                                                            <span className="text-muted">
                                                                {item.description.length > 50 
                                                                    ? `${item.description.substring(0, 50)}...` 
                                                                    : item.description
                                                                }
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="fw-bold">
                                                            {Number(item.itemPrice || 0).toFixed(2)} {item.itemCurrency || 'XOF'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {(item.discount || 0) > 0 ? (
                                                            <Badge bg="success">{item.discount}% OFF</Badge>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td>{new Date(item.created).toLocaleDateString()}</td>
                                                    <td>
                                                        <div className="d-flex gap-1 flex-wrap">
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() => handleEditItem(item)}
                                                            >
                                                                Edit
                                                            </Button>
                                                            <Button
                                                                variant="secondary"
                                                                size="sm"
                                                                onClick={() => handleItemImages(item)}
                                                            >
                                                                Images
                                                            </Button>
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                onClick={() => handleDeleteItemClick(item)}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                            {filteredItems.length === 0 && (
                                                <tr>
                                                    <td colSpan={6} className="text-center">
                                                        {itemFilter ? 'No items match your search' : 'No items found'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Collapse>
                        </Card>
                    </div>
                )}

                <ConfirmationModal
                    show={showDeleteItemModal}
                    title="Delete item?"
                    message={
                        itemToDelete
                            ? `Are you sure you want to delete "${itemToDelete.name}"? This cannot be undone.`
                            : 'Are you sure you want to delete this item?'
                    }
                    onConfirm={handleDeleteItemConfirm}
                    onCancel={handleDeleteItemCancel}
                />

                <BoutiqueItemFormModal
                    show={showItemModal}
                    handleClose={() => setShowItemModal(false)}
                    token={token}
                    boutiqueId={Number(boutiqueId)}
                    item={selectedItem}
                />

                <BoutiqueItemImageModal
                    show={showItemImageModal}
                    handleClose={() => {
                        setShowItemImageModal(false);
                        setSelectedImageItem(undefined);
                    }}
                    token={token}
                    boutiqueId={Number(boutiqueId)}
                    item={
                        selectedImageItem
                            ? boutiqueItems.find(
                                (boutiqueItem) =>
                                    boutiqueItem.boutiqueItemId === selectedImageItem.boutiqueItemId
                            ) ?? selectedImageItem
                            : undefined
                    }
                />

                <SupplyOrderFormModal
                    show={showSupplyOrderModal}
                    handleClose={() => setShowSupplyOrderModal(false)}
                    token={token}
                    boutiqueId={Number(boutiqueId)}
                    order={selectedSupplyOrder}
                />

                <CustomerOrderFormModal 
                    show={showCustomerOrderModal}
                    handleClose={() => {
                        setSelectedCustomerOrder(undefined);
                        setShowCustomerOrderModal(false);
                    }}
                    token={token}
                    boutiqueId={Number(boutiqueId)}
                    order={selectedCustomerOrder}
                />
            </Container>
        </>
    );
};

export default BoutiqueDetailsView; 